import { useState } from "react";
import {
  Drawer, Descriptions, Space, Typography, Upload, Button, Select, Input,
  Empty, Timeline, Divider, Alert, Modal,
} from "antd";
import { UploadOutlined, InboxOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import StatusTag from "../components/StatusTag";
import DocumentLink from "../components/DocumentLink";
import { DOCUMENT_TYPES } from "../constants/documentTypes";
import { CorpClaimPortalService } from "../services/corpClaimPortalService";
import { notifyUser } from "../utils/notify";

const { Text } = Typography;
const { Dragger } = Upload;

const CLOSED_STATUSES = ["APPROVED", "PAID", "CLOSED", "REJECTED"];
const fmt = (d) => (d ? dayjs(d).format("ddd, DD MMM YYYY [at] HH:mm") : "—");
const money = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }));
// The scheme's human name only ever comes from the SLAMS member snapshot
// taken at submission — there is no separate scheme-name reference sync.
const schemeName = (c) => c?.slams_member_snapshot?.scheme_information?.scheme_name?.trim();

/**
 * Full claim record, shown as a wide (85%) side drawer from the claims
 * table — mirrors the shape of EasyLife's own ExpenseDetailDrawer (Drawer →
 * Descriptions sections → Timeline), minus the approval-chain footer since
 * this is the requester's own view, not a staff processing screen.
 */
const ClaimDetailDrawer = ({ open, onClose, claim, onChanged, onWithdrawn, claimTypeLabels = {}, onEdit }) => {
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState();
  const [uploading, setUploading] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  if (!claim) return null;

  const canUpload = !CLOSED_STATUSES.includes(claim.status);
  // Matches the backend's own rule: only while nothing has started reviewing
  // it yet can the requester still correct or withdraw the claim.
  const canEditOrWithdraw = claim.status === "SUBMITTED";
  const acceptedSubmission = (claim.submissions || []).find((s) => s.status === "ACCEPTED" && s.external_claim_id);

  const handleWithdraw = () => {
    Modal.confirm({
      title: "Withdraw this claim?",
      content: "This cannot be undone. The claim and any documents attached to it will be removed.",
      okText: "Withdraw",
      okButtonProps: { danger: true, loading: withdrawing },
      onOk: async () => {
        setWithdrawing(true);
        try {
          await CorpClaimPortalService.remove(claim.id);
          notifyUser("success", "Claim withdrawn.", "Done");
          onClose();
          onWithdrawn?.();
        } catch (e) {
          notifyUser("error", e?.response?.data?.message || "Could not withdraw this claim.", "Error");
        } finally {
          setWithdrawing(false);
        }
      },
    });
  };

  const handleUpload = async (file) => {
    if (!docName.trim() || !docType) {
      notifyUser("error", "Give the document a name and type before uploading.", "Missing details");
      return false;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", docName.trim());
      formData.append("type", docType);
      await CorpClaimPortalService.uploadDocument(claim.id, formData);
      notifyUser("success", "Document uploaded.", "Uploaded");
      setDocName("");
      setDocType(undefined);
      onChanged?.();
    } catch (e) {
      notifyUser("error", e?.response?.data?.message || "Could not upload this document.", "Error");
    } finally {
      setUploading(false);
    }
    return false;
  };

  return (
    <Drawer
      title={
        <Space wrap>
          <span>CC-{String(claim.id).padStart(6, "0")}</span>
          <StatusTag status={claim.status} />
        </Space>
      }
      open={open}
      onClose={onClose}
      width="85%"
      styles={{ body: { paddingBottom: 24 } }}
      extra={canEditOrWithdraw && (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => onEdit?.(claim)}>Edit</Button>
          <Button danger icon={<DeleteOutlined />} loading={withdrawing} onClick={handleWithdraw}>Withdraw</Button>
        </Space>
      )}
    >
      <div className="container">
      {claim.status === "MISSING_DOCUMENTS" && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="Additional documents were requested for this claim." description="Please upload what is needed below." />
      )}
      {claim.status === "REJECTED" && claim.rejection_reason && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Rejected" description={claim.rejection_reason} />
      )}

      <Descriptions title={<Text strong>Claim Details</Text>} column={2} bordered size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Claimant">{claim.claimant_name}</Descriptions.Item>
        <Descriptions.Item label="Claim type">{claimTypeLabels[claim.claim_type_code] || claim.claim_type_code}</Descriptions.Item>
        <Descriptions.Item label="Scheme">{schemeName(claim) ? `${schemeName(claim)} (${claim.scheme_no})` : claim.scheme_no}</Descriptions.Item>
        <Descriptions.Item label="Staff no.">{claim.member_staff_no || "—"}</Descriptions.Item>
        <Descriptions.Item label="Submitted">{fmt(claim.submitted_at)}</Descriptions.Item>
        <Descriptions.Item label="Claimant mobile">{claim.claimant_mobile || "—"}</Descriptions.Item>
        {claim.sum_assured !== null && claim.sum_assured !== undefined && (
          <Descriptions.Item label="Sum assured (SLAMS)">{money(claim.sum_assured)}</Descriptions.Item>
        )}
        {acceptedSubmission && (
          <Descriptions.Item label="SLAMS reference">{acceptedSubmission.external_claim_id}</Descriptions.Item>
        )}
        {claim.benefit_amount !== null && claim.benefit_amount !== undefined && (
          <Descriptions.Item label="Benefit claimed">{money(claim.benefit_amount)}</Descriptions.Item>
        )}
        {claim.approved_benefit_amount !== null && claim.approved_benefit_amount !== undefined && (
          <Descriptions.Item label="Approved benefit">{money(claim.approved_benefit_amount)}</Descriptions.Item>
        )}
      </Descriptions>

      {(claim.deceased_name || claim.disability_type || claim.diagnosis) && (
        <Descriptions title={<Text strong>Claim-specific Details</Text>} column={2} bordered size="small" style={{ marginBottom: 24 }}>
          {claim.deceased_name && <Descriptions.Item label="Deceased">{claim.deceased_name}</Descriptions.Item>}
          {claim.deceased_date_of_death && <Descriptions.Item label="Date of death">{dayjs(claim.deceased_date_of_death).format("DD MMM YYYY")}</Descriptions.Item>}
          {claim.relationship_to_deceased && <Descriptions.Item label="Relationship">{claim.relationship_to_deceased}</Descriptions.Item>}
          {claim.disability_type && <Descriptions.Item label="Disability type">{claim.disability_type}</Descriptions.Item>}
          {claim.diagnosis && <Descriptions.Item label="Diagnosis">{claim.diagnosis}</Descriptions.Item>}
        </Descriptions>
      )}

      {(claim.bank_name || claim.account_number) && (
        <Descriptions title={<Text strong>Payment Details</Text>} column={2} bordered size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Bank">{claim.bank_name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Branch">{claim.branch || "—"}</Descriptions.Item>
          <Descriptions.Item label="Account name">{claim.bank_account_name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Account number">{claim.account_number || "—"}</Descriptions.Item>
        </Descriptions>
      )}

      <Divider orientation="left" plain>Documents ({claim.documents?.length || 0})</Divider>
      {claim.documents?.length ? (
        <Space direction="vertical" style={{ width: "100%", marginBottom: 20 }} size={8}>
          {claim.documents.map((d) => (
            <DocumentLink key={d.id} url={d.url} name={d.name} type={d.type} mimeType={d.mime_type} />
          ))}
        </Space>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No documents uploaded yet" style={{ margin: "16px 0" }} />
      )}

      {canUpload && (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            <Input size="large" placeholder="Document name" value={docName} onChange={(e) => setDocName(e.target.value)} style={{ width: 220 }} />
            <Select size="large" placeholder="Document type" value={docType} onChange={setDocType} style={{ width: 220 }} options={DOCUMENT_TYPES} />
          </Space>
          <Dragger beforeUpload={handleUpload} showUploadList={false} disabled={uploading} style={{ marginBottom: 24 }}>
            <p style={{ fontSize: 24, color: "#189F6B", margin: "4px 0" }}><UploadOutlined /></p>
            <p style={{ fontSize: 13, margin: 0 }}>{uploading ? "Uploading…" : "Click or drag a file here to upload"}</p>
          </Dragger>
        </>
      )}

      {claim.audit_logs?.length > 0 && (
        <>
          <Divider orientation="left" plain>Activity</Divider>
          <Timeline
            items={claim.audit_logs.map((a) => ({
              color: "green",
              children: (
                <>
                  <Text strong style={{ textTransform: "capitalize" }}>{a.action.replace(/_/g, " ")}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>{fmt(a.created_at)}</Text>
                </>
              ),
            }))}
          />
        </>
      )}
      </div>
    </Drawer>
  );
};

export default ClaimDetailDrawer;
